import { Component, Inject, OnInit, Injector, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { DocumentNode } from '@completion/models';
import { NodeFactoryService } from '../../node-factory.service';
import { NodeType } from '@completion/enums';
import { Store } from '@ngrx/store';
import { selectEditMode, getCurrentDocument, getCurrentUser, selectReviewerMode } from '@completion/selectors';
import { take, takeUntil } from 'rxjs/operators';
import { DocumentUpdateQueueService } from '../../../../core/services/document-update-queue.service';
import { Subject } from 'rxjs';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../../../shared/confirmation-dialog/confirmation-dialog.component';
import { Confirmation, Comment, User } from '@completion/models';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { AddComment } from '@completion/actions';
import { persistNodeUpdateForNode } from '../node-persistence.util';

@Component({
  selector: 'app-section',
  templateUrl: './section.component.html',
  styleUrls: ['./section.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionComponent implements OnInit, OnDestroy {
  nodes: DocumentNode[] = [];
  heading: string = '';
  chapterNumber: string = '';

  // UI state for inline add controls
  public editMode$;
  public editMode: boolean = false;
  // showingAddAt stores either a node id (insert before that node), or the string 'start' or 'end'
  public showingAddAt: number | 'start' | 'end' | null = null;
  public newNodeTypeSelection: { [pos: string]: NodeType } = {};
  public nodeTypes = [NodeType.HEADING, NodeType.PARAGRAPH, NodeType.LINK, NodeType.SECTION, NodeType.TEST_SECTION];
  private destroy$ = new Subject<void>();
  headingEditControl = new FormControl('');
  editingHeading = false;
  originalHeading = '';

  public nodeTypeLabel: { [k: string]: string } = {
    [NodeType.HEADING]: 'Heading',
    [NodeType.PARAGRAPH]: 'Paragraph',
    [NodeType.LINK]: 'Link',
    [NodeType.SECTION]: 'Section',
    [NodeType.TEST_SECTION]: 'Test Section',
  };

  public isNestedSection: boolean = false;
  // Expose enum to template (template uses NodeType.SECTION etc.)
  public NodeType = NodeType; // expose enum once for template
  // Removed NodeTypeEnum duplication to avoid maintenance overhead.
  public isSection = (node: DocumentNode | undefined | null): boolean => !!node && node.type === NodeType.SECTION;

  public reviewerMode = false;
  private currentUser: User | null = null;

  constructor(
    public nodeFactory: NodeFactoryService,
    public injector: Injector,
    @Inject('documentNode') public documentNode: DocumentNode,
    @Inject('node') public parentDocument: any,
    private store: Store,
    private updateQueue: DocumentUpdateQueueService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {}

  constructorInit() {
    // initialize observables after DI
    this.editMode$ = this.store.select(selectEditMode);
    this.editMode$.pipe(takeUntil(this.destroy$)).subscribe((v) => {
      this.editMode = !!v;
      try {
        this.cdr.markForCheck();
      } catch {
        /* ignore */
      }
    });

    // Determine if this section is nested (its parent is another section, not the root document)
    try {
      this.isNestedSection = !!this.parentDocument && (this.parentDocument as any).type === NodeType.SECTION;
    } catch {
      this.isNestedSection = false;
    }

    // Subscribe to reviewer mode from store
    this.store
      .select(selectReviewerMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((on) => {
        this.reviewerMode = !!on;
        try {
          this.cdr.markForCheck();
        } catch {}
      });
    this.store
      .select(getCurrentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((u) => {
        this.currentUser = u as any;
      });
  }

  ngOnInit(): void {
    this.nodes = this.documentNode.children || [];
    this.heading = this.computeHeading(this.documentNode, this.nodes);
    this.constructorInit();
    this.originalHeading = this.heading;
    this.headingEditControl.setValue(this.heading);

    // Subscribe to document changes so child node removals (e.g., paragraph delete) are reflected
    this.store
      .select(getCurrentDocument)
      .pipe(takeUntil(this.destroy$))
      .subscribe((doc) => {
        if (!doc) {
          return;
        }
        // Compute chapter number from the document tree
        this.chapterNumber = this.computeChapterNumber(doc.nodes || [], this.documentNode.id);

        // Find updated section node by id and refresh local nodes/headings
        const updatedSection = findNodeById(doc.nodes || [], this.documentNode.id);
        if (updatedSection) {
          this.nodes = updatedSection.children || [];
          // Update heading using updated section node (avoid mutating injected node)
          if (!this.editingHeading) {
            this.heading = this.computeHeading(updatedSection, this.nodes);
          }
          try {
            this.cdr.markForCheck();
          } catch {}
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Walk the document node tree to compute a hierarchical chapter number
   * (e.g. "1", "1.2", "3.1.1") for the section with the given targetId.
   * Only SECTION and TEST_SECTION nodes count towards numbering.
   */
  private computeChapterNumber(nodes: DocumentNode[], targetId: number, prefix: string = ''): string {
    let counter = 0;
    for (const node of nodes) {
      if (node.type === NodeType.SECTION || node.type === NodeType.TEST_SECTION) {
        counter++;
        const num = prefix ? `${prefix}.${counter}` : `${counter}`;
        if (node.id === targetId) {
          return num;
        }
        // Search children
        if (node.children?.length) {
          const found = this.computeChapterNumber(node.children, targetId, num);
          if (found) {
            return found;
          }
        }
      }
    }
    return '';
  }

  // Determine sensible default node type for this section
  public getDefaultTypeForSection(): NodeType {
    if (!this.documentNode || !this.documentNode.children || this.documentNode.children.length === 0) {
      return this.nodeTypes[0];
    }
    const counts: { [k: string]: number } = {};
    for (const c of this.documentNode.children) {
      const t = String(c.type);
      counts[t] = (counts[t] || 0) + 1;
    }
    let best = this.nodeTypes[0];
    let bestCount = 0;
    for (const nt of this.nodeTypes) {
      const key = String(nt);
      if ((counts[key] || 0) > bestCount) {
        best = nt;
        bestCount = counts[key];
      }
    }
    return best;
  }

  // Show the inline add UI at a given position key (node id, 'start', or 'end')
  public addButtonClicked(positionKey: number | 'start' | 'end'): void {
    this.showingAddAt = positionKey;
    const key = String(positionKey);
    this.newNodeTypeSelection[key] = this.getDefaultTypeForSection();
  }

  public cancelAdd(): void {
    this.showingAddAt = null;
  }

  public confirmAdd(positionKey: number | 'start' | 'end'): void {
    const key = String(positionKey);
    const type = this.newNodeTypeSelection[key] || this.getDefaultTypeForSection();
    // compute numeric insertion index
    let pos: number;
    const total = (this.documentNode.children || []).length;
    if (positionKey === 'start') {
      pos = 0;
    } else if (positionKey === 'end') {
      pos = total;
    } else if (typeof positionKey === 'number') {
      // Treat numeric key as the index position captured from the template
      pos = Math.max(0, Math.min(positionKey, total));
    } else {
      // Fallback (should not happen now): try to locate by id
      const idx = (this.documentNode.children || []).findIndex((c) => c && c.id === (positionKey as any));
      pos = idx >= 0 ? idx : total;
    }
    this.addNode(pos, type);
    this.showingAddAt = null;
  }

  // Add a sibling section immediately after this section in the top-level nodes
  public addSiblingAfter(): void {
    const newSection: DocumentNode = {
      id: this.makeId(),
      content: '',
      name: 'New Section',
      type: NodeType.SECTION,
      children: [],
    } as any;

    this.store
      .select(getCurrentDocument)
      .pipe(take(1))
      .subscribe((doc: any) => {
        if (!doc) {
          console.warn('No current document to update for sibling insert');
          return;
        }
        const updatedNodes = this.insertSiblingImmutable(doc.nodes || [], this.documentNode.id, newSection);
        if (updatedNodes === doc.nodes) {
          console.warn('Failed to insert sibling section into cloned document');
          return;
        }
        const updatedDoc = { ...doc, nodes: updatedNodes };
        this.assignOrdinals(updatedDoc);
        this.updateQueue.queue(updatedDoc);
      });
  }

  // Nested child sections drag-and-drop handler
  onChildSectionsDrop(event: CdkDragDrop<any>): void {
    if (!this.editMode || !Array.isArray(this.nodes) || !this.documentNode) {
      return;
    }
    const sectionChildIndices: number[] = this.nodes.map((n, i) => (n && n.type === NodeType.SECTION ? i : -1)).filter((i) => i >= 0);
    const fromIdx = sectionChildIndices[event.previousIndex];
    const toIdx = sectionChildIndices[event.currentIndex];
    if (fromIdx == null || toIdx == null || fromIdx === toIdx) {
      return;
    }

    this.store
      .select(getCurrentDocument)
      .pipe(take(1))
      .subscribe((doc) => {
        if (!doc) {
          return;
        }
        const cloned = JSON.parse(JSON.stringify(doc));
        const reorderChildren = (nodes: any[]): boolean => {
          for (const n of nodes) {
            if (n.id === this.documentNode.id) {
              if (!Array.isArray(n.children)) {
                return false;
              }
              const arr = n.children;
              const [moved] = arr.splice(fromIdx, 1);
              const insertIndex = fromIdx < toIdx ? toIdx - 1 : toIdx; // adjust target index if removing before target
              arr.splice(insertIndex, 0, moved);
              return true;
            }
            if (Array.isArray(n.children) && n.children.length) {
              if (reorderChildren(n.children)) {
                return true;
              }
            }
          }
          return false;
        };
        const changed = reorderChildren(cloned.nodes || []);
        if (!changed) {
          return;
        }
        this.assignOrdinals(cloned);
        // Persist reorder immediately to reflect ordering change on server/store
        this.updateQueue.queueImmediate(cloned);
      });
  }

  moveSectionToTopLevel(): void {
    if (!this.isNestedSection) {
      return;
    }
    this.store
      .select(getCurrentDocument)
      .pipe(take(1))
      .subscribe((doc) => {
        if (!doc) {
          return;
        }
        const cloned = JSON.parse(JSON.stringify(doc));
        let extracted: any = null;
        // Remove target section from anywhere in tree
        const removeAndExtract = (nodes: any[]): any[] => {
          const result: any[] = [];
          for (const n of nodes) {
            if (n.id === this.documentNode.id) {
              extracted = n;
              continue;
            }
            if (Array.isArray(n.children) && n.children.length) {
              n.children = removeAndExtract(n.children);
            }
            result.push(n);
          }
          return result;
        };
        cloned.nodes = removeAndExtract(cloned.nodes || []);
        if (!extracted) {
          return;
        }
        // Append extracted section to top-level nodes at end
        cloned.nodes.push(extracted);
        this.assignOrdinals(cloned);
        this.updateQueue.queueImmediate(cloned);
      });
  }

  // Insert a new node into the section's children at the given position and dispatch an update
  public addNode(positionIndex: number, type: NodeType): void {
    const isHeading = type === NodeType.HEADING;
    const isParagraph = type === NodeType.PARAGRAPH;
    const isLink = type === NodeType.LINK;
    const isSection = type === NodeType.SECTION;
    const isTestSection = type === NodeType.TEST_SECTION;
    const newNode: DocumentNode = {
      id: this.makeId(),
      content: isHeading ? 'New Heading' : isParagraph ? 'New paragraph' : isLink ? 'Link' : '',
      name: isHeading
        ? 'Heading'
        : isParagraph
          ? 'Paragraph'
          : isLink
            ? 'Link'
            : isSection
              ? 'New Section'
              : isTestSection
                ? 'Checks / Tests'
                : this.getHumanLabelForNodeType(type),
      type: type as any,
      children: isTestSection
        ? [
            {
              id: this.makeId() - 1,
              content: JSON.stringify({ name: '', executorCompany: null, sign: null, comments: '' }),
              name: 'Check/Test',
              type: NodeType.PROCEDURE_CHECK as any,
              children: [],
              ordinal: 0,
            },
          ]
        : [],
    } as any;

    // Clone the parent document and insert the node into the matching section
    this.store
      .select(getCurrentDocument)
      .pipe(take(1))
      .subscribe((doc: any) => {
        if (!doc) {
          console.warn('No current document to update');
          return;
        }
        const updatedNodes = this.insertIntoSectionImmutable(doc.nodes || [], this.documentNode.id, positionIndex, newNode);
        if (updatedNodes === doc.nodes) {
          console.warn('Failed to insert new node into cloned document');
          return;
        }
        const updatedDoc = { ...doc, nodes: updatedNodes };
        this.assignOrdinals(updatedDoc);
        // Queue document update to avoid dispatch storms (additions can be debounced)
        this.updateQueue.queue(updatedDoc);
      });
  }

  // Immutable insert into section: returns new nodes array if changed, otherwise returns original
  private insertIntoSectionImmutable(nodes: any[], sectionId: number, pos: number, newNode: any): any[] {
    let changed = false;

    const mapped = nodes.map((n) => {
      if (n && n.id === sectionId) {
        const newChildren = (n.children || []).slice();
        newChildren.splice(pos, 0, newNode);
        changed = true;
        return { ...n, children: newChildren };
      }
      if (n && n.children && n.children.length) {
        const updatedChildren = this.insertIntoSectionImmutable(n.children, sectionId, pos, newNode);
        if (updatedChildren !== n.children) {
          changed = true;
          return { ...n, children: updatedChildren };
        }
      }
      return n;
    });

    return changed ? mapped : nodes;
  }

  // Insert a sibling section immutably after a given id anywhere in the tree
  private insertSiblingImmutable(nodes: any[], afterId: number, newNode: any): any[] {
    if (!Array.isArray(nodes)) {
      return nodes;
    }
    const idx = nodes.findIndex((n) => n && n.id === afterId);
    if (idx >= 0) {
      const copy = nodes.slice();
      copy.splice(idx + 1, 0, newNode);
      return copy;
    }
    let changed = false;
    const mapped = nodes.map((n) => {
      if (n && Array.isArray(n.children) && n.children.length) {
        const updatedChildren = this.insertSiblingImmutable(n.children, afterId, newNode);
        if (updatedChildren !== n.children) {
          changed = true;
          return { ...n, children: updatedChildren };
        }
      }
      return n;
    });
    return changed ? mapped : nodes;
  }

  // Make this public so the template can access it
  public getHumanLabelForNodeType(type: NodeType): string {
    switch (type) {
      case NodeType.HEADING:
        return 'Heading';
      case NodeType.PARAGRAPH:
        return 'Paragraph';
      case NodeType.AUTHOR:
        return 'Author';
      case NodeType.REVIEWER:
        return 'Reviewer';
      case NodeType.APPROVER:
        return 'Approver';
      case NodeType.CP_SELECTOR:
        return 'CP Selector';
      case NodeType.SECTION:
        return 'Section';
      case NodeType.TEST_SECTION:
        return 'Test Section';
      case NodeType.PROCEDURE_CHECK:
        return 'Check/Test';
      default:
        return String(type);
    }
  }

  private computeHeading(section: DocumentNode | null | undefined, children: DocumentNode[] | undefined): string {
    // Prefer explicit name if provided on the given section
    const named = section && typeof section.name === 'string' ? section.name.trim() : '';
    if (named) {
      return named;
    }

    // Otherwise, infer from children: if a HEADING child exists, use its content
    if (children && children.length) {
      const firstHeading = children.find((n) => n.type === NodeType.HEADING);
      if (firstHeading && typeof firstHeading.content === 'string' && firstHeading.content.trim()) {
        return firstHeading.content.trim();
      }
      // Fallback heuristic labels based on child types
      const types = new Set(children.map((n) => n.type));
      if (types.has(NodeType.AUTHOR)) {
        return 'Authors';
      }
      if (types.has(NodeType.REVIEWER)) {
        return 'Reviewers';
      }
      if (types.has(NodeType.APPROVER)) {
        return 'Approvers';
      }
      if (types.has(NodeType.TABLE_OF_CONTENTS)) {
        return 'Table of Contents';
      }
      if (types.has(NodeType.CP_SELECTOR)) {
        return 'CP Selector';
      }
      if (types.has(NodeType.PROCEDURE_CHECK)) {
        return 'Checks / Tests';
      }
    }

    // No explicit name or useful children; return empty and let template show a placeholder
    return '';
  }

  // Generate a temporary negative id for new local nodes
  private makeId(): number {
    return Date.now() * -1;
  }

  // Begin editing the section heading (only in edit mode)
  startEditHeading(): void {
    if (!this.editMode) return;
    this.editingHeading = true;
    this.originalHeading = this.heading;
    // Seed control with current display heading (or placeholder) for a better UX
    const seed = this.heading && this.heading.trim() ? this.heading : 'New Section';
    this.headingEditControl.setValue(seed);
    try {
      this.cdr.markForCheck();
    } catch {}
  }

  // Cancel heading edit, restore original
  cancelEditHeading(): void {
    this.editingHeading = false;
    this.headingEditControl.setValue(this.originalHeading);
    try {
      this.cdr.markForCheck();
    } catch {}
  }

  // Save heading edit and persist immutably
  saveEditHeading(): void {
    const newHeading = (this.headingEditControl.value || '').trim();
    this.editingHeading = false;
    // Optimistically reflect the new heading in UI; underlying store update will confirm
    this.heading = newHeading;

    // Persist via immutable update of the document without mutating injected node
    this.store
      .select(getCurrentDocument)
      .pipe(take(1))
      .subscribe((doc: any) => {
        if (!doc) {
          return;
        }
        const cloned = JSON.parse(JSON.stringify(doc));
        const updateNodeName = (nodes: any[]): boolean => {
          for (const n of nodes) {
            if (n && n.id === (this.documentNode as any).id) {
              n.name = newHeading;
              return true;
            }
            if (n && n.children && n.children.length) {
              if (updateNodeName(n.children)) {
                return true;
              }
            }
          }
          return false;
        };
        updateNodeName(cloned.nodes || []);
        this.updateQueue.queue(cloned);
      });
    try {
      this.cdr.markForCheck();
    } catch {}
  }

  // Handle Enter key or blur commit logic
  onHeadingInputKey(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveEditHeading();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEditHeading();
    }
  }

  onHeadingBlur(): void {
    if (this.editingHeading) {
      this.saveEditHeading();
    }
  }

  removeSection(): void {
    const data: Confirmation = {
      title: 'Delete section',
      message: 'Are you sure you want to delete this section? This action cannot be undone.',
      confirmationMessage: 'Delete',
      warning: 'This will remove the section and all of its contents.',
      icon: 'delete',
      isCancelDisplay: true,
      confirmationTitle: 'Confirm',
    };

    const ref = this.dialog.open(ConfirmationDialogComponent, { data });
    ref
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }
        this.store
          .select(getCurrentDocument)
          .pipe(take(1))
          .subscribe((doc) => {
            if (!doc) {
              return;
            }
            const { nodes: updatedNodes, removed } = this.removeNodeByIdImmutable(doc.nodes || [], this.documentNode.id);
            if (!removed) {
              return;
            }
            const updatedDoc = { ...doc, nodes: updatedNodes };
            this.assignOrdinals(updatedDoc);
            this.updateQueue.queue(updatedDoc);
          });
      });
  }

  private removeNodeByIdImmutable(nodes: any[], id: number): { nodes: any[]; removed: boolean } {
    if (!Array.isArray(nodes) || nodes.length === 0) {
      return { nodes, removed: false };
    }
    let removed = false;
    const next: any[] = [];
    for (const n of nodes) {
      if (!n) {
        continue;
      }
      if (n.id === id) {
        removed = true; // delete this node
        continue;
      }
      let updatedChildren = n.children;
      if (Array.isArray(n.children) && n.children.length) {
        const res = this.removeNodeByIdImmutable(n.children, id);
        if (res.removed) {
          updatedChildren = res.nodes;
          removed = true;
        }
      }
      next.push(updatedChildren !== n.children ? { ...n, children: updatedChildren } : n);
    }
    return removed ? { nodes: next, removed: true } : { nodes, removed: false };
  }

  // Assign ordinal values for ordering across the document tree
  private assignOrdinals(doc: any): void {
    const walk = (nodes: any[] | undefined) => {
      if (!Array.isArray(nodes)) {
        return;
      }
      nodes.forEach((n, i) => {
        if (!n || typeof n !== 'object') {
          return;
        }
        n.ordinal = i;
        if (Array.isArray(n.children) && n.children.length) {
          walk(n.children);
        }
      });
    };
    try {
      walk(doc?.nodes);
    } catch {}
  }

  addComment(targetNode: DocumentNode): void {
    if (!this.reviewerMode) return;
    const text = window.prompt('Enter comment');
    if (!text || !text.trim()) return;
    const payload: Partial<Comment> = {
      content: text.trim(),
      createdAt: new Date().toISOString(),
      author: this.currentUser as any,
    } as any;
    // Let reducers handle adding comment to the node, then persist full document using helper
    this.store.dispatch(new AddComment(targetNode.id, payload as any));
    persistNodeUpdateForNode(this.store, this.updateQueue, targetNode, () => {});
  }
}

// Helper to locate node by id in a tree
function findNodeById(nodes: any[], id: number): any {
  for (const n of nodes) {
    if (n && n.id === id) {
      return n;
    }
    if (n && n.children && n.children.length) {
      const found = findNodeById(n.children, id);
      if (found) {
        return found;
      }
    }
  }
  return null;
}
